import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";

import { ActionButton, InputField, SelectField } from "../v3";
import { CRMService, ProjectService } from "../../services";
import { receivablesValidationRules } from "../../pages/client/validation";
import { get_date } from "../generalComponets";
import { ENABLE_CLIENT_PAYMENT_BACKDATE_ENTRY } from "../../constants";
import { useHasPermission } from "../../utils";
import { markTableReset } from "../../utils/tableStateStorage";

export const AddEditReceivablesForm = () => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [receivablesDetails, setReceivablesDetails] = useState({
    amount: undefined,
    date: get_date(Date.now(), "YYYY-MM-DD"),
    description: undefined,
    expectedDate: undefined,
    projectId: null,
  });

  const enableBackDate = useHasPermission(ENABLE_CLIENT_PAYMENT_BACKDATE_ENTRY);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("clientId");
  const { id } = useParams();

  useEffect(() => {
    if (id) fetchReceivablesDetails(id);
    fetchData(clientId);
  }, [clientId, id]);

  const fetchData = async (clientId) => {
    if (!clientId) return;
    try {
      const { data } = await CRMService.getClientById(clientId);
      setSelectedClient(data);
    } catch (error) {
      toast.error("Failed to fetch clients data");
    }
  };

  const fetchReceivablesDetails = async (id) => {
    try {
      const receivablesResponse = await CRMService.getClientReceivablesById(id);

      setSelectedClient(receivablesResponse?.data?.client);
      setSelectedProject(receivablesResponse?.data?.project);
      setReceivablesDetails({
        amount: Number(receivablesResponse?.data?.amount),
        date: get_date(receivablesResponse?.data?.date, "YYYY-MM-DD"),
        description: receivablesResponse?.data?.description,
        projectId: receivablesResponse?.data?.projectId,
      });
    } catch (error) {
      toast.error("Failed to fetch payment details"); // TODO: payment should be replaced with meaningful word
    }
  };

  const handleClientChange = (selected) => {
    setSelectedClient(selected);
  };

  const handleProjectChange = (selected) => {
    setSelectedProject(selected);
    setReceivablesDetails((prev) => ({
      ...prev,
      projectId: selected?.id,
    }));
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setReceivablesDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCancelClick = () => {
    setErrors({});
    setReceivablesDetails({
      amount: undefined,
      date: get_date(Date.now(), "YYYY-MM-DD"),
      description: undefined,
      expectedDate: undefined,
      projectId: null,
    });
    if (clientId || id) navigate(-1);
  };

  // TODO: to be implemented
  const handleReceivedChange = () => setReceivablesDetails({ ...receivablesDetails, isReceived: !receivablesDetails.isReceived });

  const handleSaveReceivables = async () => {
    setErrors({});
    const errors = receivablesValidationRules(selectedClient?.id, receivablesDetails);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    receivablesDetails.amount = Number(receivablesDetails.amount);
    receivablesDetails.date = moment(`${receivablesDetails.date} ${moment().format("HH:mm:ss")}`).toISOString();

    try {
      setLoading(true);
      if (id) {
        await CRMService.updateClientReceivables(selectedClient?.id, id, receivablesDetails);
        toast.success("Payment updated successfully"); // TODO: payment should be replaced with meaningful word
      } else {
        await CRMService.createClientReceivables(selectedClient?.id, receivablesDetails);
        toast.success("Payment added successfully"); // TODO: payment should be replaced with meaningful word
        // The client's statement opens fresh, so its filters don't hide the new payment.
        if (clientId) markTableReset();
      }
      handleCancelClick();
    } catch (error) {
      let errorMessage = `Error ${id ? "updating" : "adding"} payment`; // TODO: payment should be replaced with meaningful word
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast.error(errorMessage);
      console.error("🚀 ~ handleSavePayment ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box className="fl gap-2">
        <SelectField
          label="Client"
          value={selectedClient}
          getData={CRMService.getClientList}
          getDataStaticFilters={{ active: true }}
          disabled={true} // assuming client can't be changed when editing
          required
          error={!!errors.subcontractorId}
          helperText={errors.subcontractorId}
          onChange={handleClientChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="Project"
          value={selectedProject}
          getData={ProjectService.getProjectList}
          getDataStaticFilters={{ status: ["INPROGRESS"], clientId: selectedClient?.id }}
          required
          error={!!errors.projectId}
          helperText={errors.projectId}
          onChange={handleProjectChange}
          style={{ width: "100%" }}
        />
      </Box>
      <Box className="fl gap-2">
        <InputField
          required
          label="Date"
          name="date"
          type="date"
          value={receivablesDetails.date}
          onChange={updateDetailsState}
          error={!!errors.date}
          helperText={errors.date}
          fullWidth
          inputSlotProps={{
            htmlInput: {
              min: enableBackDate ? undefined : get_date(Date.now(), "YYYY-MM-DD"),
              onKeyDown: enableBackDate ? undefined : (e) => e.preventDefault(),
            },
          }}
        />
        <InputField
          required
          label="Amount"
          name="amount"
          type="number"
          value={receivablesDetails.amount}
          onChange={updateDetailsState}
          error={!!errors.amount}
          helperText={errors.amount}
          fullWidth
        />
        <InputField
          label="Description"
          name="description"
          value={receivablesDetails.description}
          onChange={updateDetailsState}
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
        />
      </Box>

      {/* TODO: add received functionality */}
      {/* {!id && (
        <>
          <Box display="flex" alignItems="center" gap={2} >
            <Typography variant="subtitle2" gutterBottom>
              Received
            </Typography>
            <CSwitch checked={receivablesDetails.isReceived} onChange={handleReceivedChange} />
          </Box>

          {receivablesDetails.isReceived && (
            <Box className="fl gap-2" >
              <InputField
                label="Received Amount"
                name="receivedAmount"
                type="number"
                value={receivablesDetails.receivedAmount}
                onChange={handleChange}
                error={!!errors.receivedAmount}
                helperText={errors.receivedAmount}
                fullWidth
              />
              <InputField
                select={true}
                selectOptions={accounts}
                label="Account"
                name="toAccountId"
                value={receivablesDetails.toAccountId}
                onChange={handleChange}
                error={!!errors.toAccountId}
                helperText={errors.toAccountId}
                fullWidth
              />
            </Box>
          )}
        </>
      )} */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <ActionButton buttonText="Cancel" onButtonClick={handleCancelClick} variant="outlined" />
        <ActionButton buttonText="Save" onButtonClick={handleSaveReceivables} disabled={loading} />
      </Box>
    </>
  );
};
